import { createClient } from '@/lib/supabase/client';

export interface HelpArticle {
  id: string;
  category: string;
  categoryOrder: number;
  title: string;
  bodyMarkdown: string;
  sortOrder: number;
  updatedAt: string;
}

export interface HelpCategory {
  name: string;
  order: number;
  articles: HelpArticle[];
}

interface HelpArticleRow {
  id: string;
  category: string;
  category_order: number | null;
  title: string;
  body_markdown: string | null;
  sort_order: number | null;
  updated_at: string;
}

function mapRow(row: HelpArticleRow): HelpArticle {
  return {
    id: row?.id,
    category: row?.category ?? '',
    categoryOrder: row?.category_order ?? 0,
    title: row?.title ?? '',
    bodyMarkdown: row?.body_markdown ?? '',
    sortOrder: row?.sort_order ?? 0,
    updatedAt: row?.updated_at,
  };
}

export function groupByCategory(articles: HelpArticle[]): HelpCategory[] {
  const map = new Map<string, HelpCategory>();
  (articles ?? []).forEach((a) => {
    const existing = map.get(a.category);
    if (existing) {
      existing.articles.push(a);
      if (a.categoryOrder < existing.order) existing.order = a.categoryOrder;
    } else {
      map.set(a.category, { name: a.category, order: a.categoryOrder, articles: [a] });
    }
  });
  const list = Array.from(map.values());
  list.forEach((c) => c.articles.sort((x, y) => x.sortOrder - y.sortOrder || x.title.localeCompare(y.title)));
  list.sort((x, y) => x.order - y.order || x.name.localeCompare(y.name));
  return list;
}

export const helpService = {
  async listPublished(): Promise<HelpArticle[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('help_articles')
      .select('id, category, category_order, title, body_markdown, sort_order, updated_at')
      .eq('published', true)
      .order('category_order', { ascending: true })
      .order('sort_order', { ascending: true });
    if (error) throw new Error(error.message);
    return ((data as HelpArticleRow[]) ?? []).map(mapRow);
  },

  async getPublished(id: string): Promise<HelpArticle | null> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('help_articles')
      .select('id, category, category_order, title, body_markdown, sort_order, updated_at')
      .eq('id', id)
      .eq('published', true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return data ? mapRow(data as HelpArticleRow) : null;
  },
};
